import React, { useEffect, useState, useRef, useMemo } from 'react';

import useFetchDoxExt from '../hooks/useFetchDoxExt';
import Loader from './Loader';

import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import MsgReader from '@kenjiuno/msgreader';
import * as XLSX from "xlsx";

import pako from 'pako';
import { RTFJS } from 'rtf.js';

const createPdfBlobUrl = (base64) => {
    const byteCharacters = atob(base64);
    const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
};

const arrayBufferToBase64 = (uint8Array) => {
    let binary = '';
    const bytes = new Uint8Array(uint8Array);
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
};

const parseSenderName = (sender) => {
    const x500Regex = /CN=([^/]+)/;
    const match = sender.match(x500Regex);
    if (match && match[1]) {
        return match[1];
    }
    return sender;
};

const transformEmailBody = (body) => {
    if (!body) return "";
    let transformedBody = body.replace(/\r\n+/g, "<br>");
    transformedBody = transformedBody.replace(/(<br>){2,}/g, "<br>");
    return transformedBody;
};

const parseEmailDate = (headers) => {
    const dateRegex = /Date:\s*(.+?)(\r\n|$)/i;
    const match = headers.match(dateRegex);
    if (match && match[1]) {
        const rawDate = match[1].trim();
        const parsedDate = new Date(rawDate);
        return parsedDate.toLocaleString("fr-FR", {
            dateStyle: "long",
            timeStyle: "short"
        });
    }
    return "Date inconnue";
};

const parseMSGFile = (fileBase64) => {
    try {
        const msgFileBuffer = new Uint8Array(atob(fileBase64).split("").map(char => char.charCodeAt(0)));

        const testMsg = new MsgReader(msgFileBuffer);
        const testMsgInfo = testMsg.getFileData();

        const subject = testMsgInfo.subject || "Sans sujet";
        const senderName = parseSenderName(testMsgInfo.senderName || "<Inconnu>");
        const senderEmail = testMsgInfo.creatorSMTPAddress || "<Inconnu>";
        const recipients = testMsgInfo.recipients || [];
        const body = transformEmailBody(testMsgInfo.body || "");
        const headers = testMsgInfo.headers || "";
        const attachments = testMsgInfo.attachments.map((attachment) => {
            const testMsgAttachment = testMsg.getAttachment(attachment);
            const base64Attachment = arrayBufferToBase64(testMsgAttachment.content);

            return {
                base64: base64Attachment,
                mimeTag: attachment.attachMimeTag,
                extension: attachment.extension,
                fileName: testMsgAttachment.fileName
            };
        }) || [];

        console.log("Email parsed:", {
            subject,
            senderName,
            senderEmail,
            recipients,
            body,
            headers,
            attachments
        });

        return ({
            subject,
            senderName,
            senderEmail,
            recipients,
            body,
            headers,
            attachments
        });
    } catch (error) {
        console.error("Erreur lors de l'analyse du fichier MSG: " + error.message);
    }
};

const downloadAttachment = (attachment) => {
    try {
        const byteCharacters = atob(attachment.base64);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i);
        }

        const blob = new Blob([byteArray], { type: attachment.mimeTag });

        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = attachment.fileName;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error("Erreur lors du téléchargement de la pièce jointe :", err);
        alert("Impossible de télécharger cette pièce jointe.");
    }
};

const parseExcelFile = (base64) => {
    try {
        const binaryString = atob(base64);
        const binaryLen = binaryString.length;
        const bytes = new Uint8Array(binaryLen);

        for (let i = 0; i < binaryLen; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        const workbook = XLSX.read(bytes, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        return data;
    } catch (error) {
        console.error("Erreur lors de l'analyse du fichier Excel :", error.message);
        return null;
    }
};

const parseTextFile = (base64Content) => {
    try {
        const byteCharacters = atob(base64Content);

        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i);
        }

        const decoder = new TextDecoder("utf-8");
        const decodedContent = decoder.decode(byteArray);

        const lines = decodedContent.split("\n");

        return { body: lines.join("\n") };
    } catch (error) {
        console.error("Erreur lors du parsing du fichier texte :", error);
        return { fileName: "Erreur", date: "Indisponible", body: "Impossible de lire le fichier." };
    }
};

// Normalise les infos sur un document 
const normalizeDocument = (doc) => {
    if (!doc) return null;

    const typeRaw =
        doc.type ||
        doc.Type_Document ||
        doc.Type ||
        "";

    const name =
        doc.name ||
        doc.Nom_Du_Document ||
        "Document";

    const dateCreated =
        doc.dateCreated ||
        doc.Date_Crea ||
        doc.Date_Modif ||
        null;

    const uuid =
        doc.UUID_ ||
        doc.uuidDoc ||
        null;

    return {
        uuid,
        type: typeRaw.toString().toUpperCase(),
        name,
        dateCreated,
        raw: doc,
    };
};

const DocumentViewerModal = ({ isVisible, onClose, selectedDocument, lstDoxExt = [] }) => {
    const { fetchBlobBase64ByUUID } = useFetchDoxExt();
    const [base64Content, setBase64Content] = useState(null);

    const pdfViewerRef = useRef(null);
    const [showAllAttachments, setShowAllAttachments] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [activeAttachment, setActiveAttachment] = useState(null);
    const [attachmentBase64, setAttachmentBase64] = useState(null);
    const viewerRef = useRef();

    const normalizedDoc = useMemo(
        () => normalizeDocument(selectedDocument),
        [selectedDocument]
    );
    const noteAttachments = useMemo(() => {
        if (!normalizedDoc?.uuid || !Array.isArray(lstDoxExt)) return [];
        return lstDoxExt.filter(
            (doc) => doc.UUID_Externe_ === normalizedDoc.uuid
        );
    }, [normalizedDoc?.uuid, lstDoxExt]);
    const lastUuidRef = useRef(null);

    useEffect(() => {
        if (!normalizedDoc?.uuid) return;

        if (lastUuidRef.current === normalizedDoc.uuid && base64Content) {
            return;
        }

        lastUuidRef.current = normalizedDoc.uuid;
        setBase64Content(null);
        setShowAllAttachments(false);

        const loadDocument = async () => {
            try {
                const b64 = await fetchBlobBase64ByUUID(normalizedDoc.uuid);
                setBase64Content(b64);
            } catch (err) {
                console.error("Erreur lors du chargement du blob :", err);
            }
        };
        loadDocument();

        return () => {
            if (pdfViewerRef.current && pdfViewerRef.current?.viewerBase) {
                try {
                    pdfViewerRef.current.destroy();
                } catch (err) {
                    console.warn("Erreur lors du destroy du PDF Viewer :", err);
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [normalizedDoc?.uuid]);

    useEffect(() => {
        if (!activeAttachment && noteAttachments.length > 0) {
            setActiveAttachment(noteAttachments[0]);
        }
    }, [noteAttachments, activeAttachment]);

    useEffect(() => {
        const attachmentUuid = activeAttachment?.UUID_;
        if (!attachmentUuid) return;

        setAttachmentBase64(null);
        (async () => {
            const b64 = await fetchBlobBase64ByUUID(attachmentUuid);
            setAttachmentBase64(b64);
        })();
    }, [activeAttachment]);

    const handleClose = () => {
        setActiveAttachment(null);
        setAttachmentBase64(null);
        setShowAllAttachments(false);
        onClose();
    };

    const emailContent = useMemo(() => {
        if (normalizedDoc?.type === "MSG" && base64Content) {
            return parseMSGFile(base64Content);
        }
        return null;
    }, [normalizedDoc, base64Content]);

    const truncateFileName = (name, maxLength = 20) => {
        if (!name) return "";

        const dotIndex = name.lastIndexOf(".");
        if (dotIndex === -1) {
            return name.length > maxLength
                ? name.slice(0, maxLength - 3) + "..."
                : name;
        }

        const base = name.slice(0, dotIndex);
        const ext = name.slice(dotIndex);

        const maxBaseLength = maxLength - ext.length - 3;
        if (maxBaseLength <= 0) {
            return name.length > maxLength
                ? name.slice(0, maxLength - 3) + "..."
                : name;
        }

        const truncatedBase =
            base.length > maxBaseLength
                ? base.slice(0, maxBaseLength) + "..."
                : base;

        return truncatedBase + ext;
    };

    const renderDocumentContent = () => {
        if (!base64Content) {
            return (
                <div className="flex justify-center items-center w-full h-full">
                    <Loader />
                </div>
            );
        }
        const handleAttachmentClick = async (attachmentDoc) => {
            try {
                const base64 = await fetchBlobBase64ByUUID(attachmentDoc.UUID_);
                if (!base64) return;

                const type = (attachmentDoc.Type_Document || attachmentDoc.Suffix || "").toUpperCase();

                const fileTypeMapping = {
                    "PDF": "application/pdf",
                    "JPEG": "image/jpeg",
                    "PNG": "image/png",
                    "WEBP": "image/webp",
                    "SVG": "image/svg+xml",
                    "TXT": "text/plain; charset=utf-8",
                    "MSG": "application/vnd.ms-outlook",
                    "XLSX": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "XLS": "application/vnd.ms-excel",
                    "VND.MS-EXCEL": "application/vnd.ms-excel",
                };

                const mimeType = fileTypeMapping[type] || "application/octet-stream";
                const originalName = attachmentDoc.Nom_Du_Document || "document";
                const byteCharacters = atob(base64);
                const byteArray = new Uint8Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteArray[i] = byteCharacters.charCodeAt(i);
                }

                const blob = new Blob([byteArray], { type: mimeType });
                const url = URL.createObjectURL(blob);

                const a = document.createElement("a");
                a.href = url;
                a.download = originalName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch (err) {
                console.error("Erreur lors du téléchargement de la pièce jointe de note :", err);
                alert("Impossible de télécharger cette pièce jointe.");
            }
        };

        if (!normalizedDoc) {
            return <p>Aucun document sélectionné.</p>;
        }

        const type = normalizedDoc.type;
        if (!type) {
            return <p>Impossible de déterminer le type du document.</p>;
        }

        if (type.startsWith("PDF")) {
            const pdfUrl = createPdfBlobUrl(base64Content);
            return (
                <div className="w-full h-full min-h-[30rem]">
                    <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                        <Viewer fileUrl={pdfUrl} />
                    </Worker>
                </div>
            );
        } else if (["JPEG", "PNG", "WEBP", "SVG"].includes(type)) {
            return (
                <div className="flex justify-center items-center w-full h-full">
                    <img
                        src={`data:image/${type.toLowerCase()};base64,${base64Content}`}
                        alt="Document"
                        className="bg-gray-100 p-10 rounded-lg max-w-full max-h-full"
                        style={{ objectFit: "contain" }}
                    />
                </div>
            );
        } else if (type === "MSG") {
            return (
                <div className="flex flex-col bg-gray-100 shadow rounded-lg h-full email-container">
                    {emailContent && (
                        <>
                            <div className="bg-gray-200 p-4 border-gray-300 border-b rounded-t-lg email-header">
                                <h3 className="font-bold text-gray-800 text-lg">
                                    Sujet : {emailContent.subject}
                                </h3>
                                <p className="text-gray-600 text-sm">
                                    <strong>De :</strong> {emailContent.senderName} ({emailContent.senderEmail})
                                </p>
                                <p className="text-gray-600 text-sm">
                                    <strong>À :</strong>{" "}
                                    {emailContent.recipients
                                        .map((rec, index) => `${parseSenderName(rec.name)} <${rec.smtpAddress}>`)
                                        .join(", ")}
                                </p>
                                <p className="text-gray-600 text-sm">
                                    <strong>Date :</strong> {parseEmailDate(emailContent.headers)}
                                </p>
                            </div>

                            <div
                                className="flex-1 px-10 py-6 overflow-y-auto text-gray-800 email-body"
                                dangerouslySetInnerHTML={{ __html: emailContent.body }}
                            />

                            {emailContent.attachments && emailContent.attachments.length > 0 && (
                                <div className="bg-gray-200 p-4 border-t">
                                    <h4 className="mb-2 font-bold text-gray-800 text-sm">Pièces jointes :</h4>

                                    <div className="flex gap-4 overflow-x-auto">
                                        {emailContent.attachments.slice(0, 5).map((attachment, index) => (
                                            <div
                                                key={index}
                                                className="bg-white shadow px-3 py-2 rounded text-sm whitespace-nowrap"
                                            >
                                                <button
                                                    onClick={() => downloadAttachment(attachment)}
                                                    className="text-blue-500 hover:underline"
                                                    title={attachment.fileName}
                                                >
                                                    {attachment.fileName.length > 30
                                                        ? `${attachment.fileName.slice(0, 28)}…`
                                                        : attachment.fileName}
                                                </button>
                                            </div>
                                        ))}

                                        {!showAllAttachments && emailContent.attachments.length > 5 && (
                                            <div className="flex items-center">
                                                <button
                                                    onClick={() => setShowAllAttachments(true)}
                                                    className="text-gray-600 hover:text-gray-800 text-sm underline"
                                                >
                                                    +{emailContent.attachments.length - 5} autres
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {showAllAttachments && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {emailContent.attachments.slice(5).map((attachment, index) => (
                                                <div
                                                    key={index}
                                                    className="bg-white shadow px-3 py-2 rounded text-sm"
                                                >
                                                    <button
                                                        onClick={() => downloadAttachment(attachment)}
                                                        className="text-blue-500 hover:underline"
                                                    >
                                                        {attachment.fileName}
                                                    </button>
                                                </div>
                                            ))}
                                            <div className="mt-2 w-full text-left">
                                                <button
                                                    onClick={() => setShowAllAttachments(false)}
                                                    className="text-gray-600 hover:text-gray-800 text-sm underline"
                                                >
                                                    Afficher moins
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            );
        } else if (["XLSX", "XLS", "VND.MS-EXCEL"].includes(type)) {
            const excelData = parseExcelFile(base64Content);

            if (!excelData) {
                return <p>Impossible de lire le fichier Excel.</p>;
            }

            return (
                <div className="bg-gray-100 p-4 rounded-lg overflow-auto">
                    <table className="border border-gray-300 w-full text-sm border-collapse table-auto">
                        <thead>
                            {excelData[0] && (
                                <tr>
                                    {excelData[0].map((header, index) => (
                                        <th
                                            key={index}
                                            className="bg-gray-200 px-4 py-2 border border-gray-300"
                                        >
                                            {header || "Colonne " + (index + 1)}
                                        </th>
                                    ))}
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {excelData.slice(1).map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((cell, cellIndex) => (
                                        <td
                                            key={cellIndex}
                                            className="px-4 py-2 border border-gray-300 text-center"
                                        >
                                            {cell}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        } else if (["TXT", "PLAIN", "PLAIN; CHARSET=UTF-8"].includes(type)) {
            const textContent = parseTextFile(base64Content);

            return (
                <div className="flex flex-col bg-gray-100 shadow rounded-lg h-full text-file-container">
                    {textContent && (
                        <>
                            <div className="bg-gray-200 p-4 border-gray-300 border-b rounded-t-lg text-file-header">
                                <h3 className="font-bold text-gray-800 text-lg">
                                    Nom du fichier : {normalizedDoc.name}
                                </h3>
                            </div>

                            <div className="flex-1 p-4 overflow-y-auto text-file-body text-gray-800 h-96">
                                <pre className="whitespace-pre-wrap text-lg">
                                    {textContent.body}
                                </pre>
                            </div>


                            {noteAttachments.length > 0 && (
                                <div className="bg-gray-200 p-4 border-t border-gray-300">
                                    <h4 className="mb-2 font-bold text-gray-800 text-sm">
                                        Documents attachés
                                    </h4>

                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {noteAttachments.map(att => (
                                            <button
                                                key={att.UUID_}
                                                type="button"
                                                onClick={() => setActiveAttachment(att)}
                                                className={
                                                    "px-3 py-1 rounded text-sm " +
                                                    (activeAttachment?.UUID_ === att.UUID_
                                                        ? "bg-orange-600 text-white"
                                                        : "bg-white text-gray-800 hover:bg-gray-100")
                                                }
                                            >
                                                {truncateFileName(att.Nom_Du_Document)}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="bg-white rounded-lg p-2 h-96 overflow-hidden">
                                        {renderAttachmentContent()}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            );
        } else {
            return <p>Visualisation non disponible pour ce type de fichier.</p>;
        }
    };
    const renderAttachmentContent = () => {
        if (!activeAttachment || !attachmentBase64) {
            return <Loader />;
        }

        const typeRaw =
            (activeAttachment.Type_Document ||
                activeAttachment.Suffix ||
                activeAttachment.type ||
                "").toString().toUpperCase();

        if (typeRaw.startsWith("PDF")) {
            const pdfUrl = createPdfBlobUrl(attachmentBase64);
            return (
                <div className="w-full h-96">
                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                        <Viewer fileUrl={pdfUrl} />
                    </Worker>
                </div>
            );
        }

        if (["JPEG", "JPG", "PNG", "WEBP", "SVG"].includes(typeRaw)) {
            return (
                <div className="flex justify-center items-center w-full h-96">
                    <img
                        src={`data:image/${typeRaw.toLowerCase()};base64,${attachmentBase64}`}
                        alt="Pièce jointe"
                        className="max-w-full max-h-full object-contain rounded-lg bg-gray-100 p-4"
                    />
                </div>
            );
        }

        if (["TXT", "PLAIN", "PLAIN; CHARSET=UTF-8"].includes(typeRaw)) {
            const textContent = parseTextFile(attachmentBase64);
            return (
                <div className="bg-gray-100 p-4 rounded-lg h-96 overflow-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800">
                        {textContent.body}
                    </pre>
                </div>
            );
        }

        return <p>Prévisualisation non disponible pour ce type de fichier.</p>;
    };

    const handleDownload = async () => {
        if (!base64Content || !normalizedDoc) return;

        const fileTypeMapping = {
            "PDF": "application/pdf",
            "JPEG": "image/jpeg",
            "PNG": "image/png",
            "WEBP": "image/webp",
            "SVG": "image/svg+xml",
            "TXT": "text/plain; charset=utf-8",
            "MSG": "application/vnd.ms-outlook",
            "XLSX": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "XLS": "application/vnd.ms-excel",
            "VND.MS-EXCEL": "application/vnd.ms-excel",
        };

        const originalName = normalizedDoc.name || 'document';
        const nameParts = originalName.split(".");
        const fileExtension =
            nameParts.length > 1 ? nameParts.pop() : normalizedDoc.type.toLowerCase();
        const cleanName = nameParts.join(".") || 'document';

        const fileName = `${cleanName}.${fileExtension}`;
        const mimeType = fileTypeMapping[normalizedDoc.type] || "application/octet-stream";

        const byteCharacters = atob(base64Content);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i);
        }

        const file = new File([byteArray], fileName, { type: mimeType });
        const url = URL.createObjectURL(file);

        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handlePrint = () => {
        if (!base64Content || !normalizedDoc) return;

        const isMobile = /android|iphone|ipad/i.test(navigator.userAgent);
        const isAndroidChrome = /android/i.test(navigator.userAgent) && /chrome/i.test(navigator.userAgent);

        if (isAndroidChrome) {
            alert("L'impression est désactivée sur Chrome Android. Veuillez télécharger le document.");
            return;
        }

        if (normalizedDoc.type.includes("PDF")) {
            const byteCharacters = atob(base64Content);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteArray[i] = byteCharacters.charCodeAt(i);
            }

            const fileBlob = new Blob([byteArray], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(fileBlob);

            const printWindow = window.open(blobUrl, '_blank');
            if (!printWindow) {
                alert("Impossible d'ouvrir la fenêtre d'impression.");
                return;
            }

            const interval = setInterval(() => {
                if (printWindow.document.readyState === 'complete') {
                    clearInterval(interval);
                    printWindow.focus();
                    printWindow.print();
                }
            }, 500);
        } else if (viewerRef.current) {
            const contentToPrint = viewerRef.current.innerHTML;
            const title = normalizedDoc.name || 'Document';

            const printWindow = window.open('', '_blank', 'width=800,height=600');
            if (printWindow) {
                printWindow.document.open();
                printWindow.document.write(`
                <html>
                    <head>
                        <title>${title}</title>
                        <style>
                            body { font-family: sans-serif; padding: 20px; }
                            img { max-width: 100%; height: auto; }
                            table { width: 100%; border-collapse: collapse; }
                            td, th { border: 1px solid #ccc; padding: 8px; text-align: left; }
                        </style>
                    </head>
                    <body>${contentToPrint}</body>
                </html>
            `);
                printWindow.document.close();

                printWindow.focus();
                setTimeout(() => {
                    printWindow.print();
                    printWindow.close();
                }, 500);
            } else {
                alert("Impossible d'ouvrir la fenêtre d'impression.");
            }
        }
    };

    const fileName = normalizedDoc?.name || 'Document';
    const fileDate = normalizedDoc?.dateCreated || null;



    return (
        <div
            className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
            onClick={(e) => {
                const selection = window.getSelection();
                if (selection && selection.type === "Range") return;
                if (e.target === e.currentTarget) handleClose();
            }}
        >
            <div
                className={`
                    relative bg-white transform transition-all duration-700 ease-in-out overflow-hidden flex flex-col
                    ${isFullscreen
                        ? 'w-[95vw] h-[95vh] rounded-none p-8'
                        : 'w-full sm:max-w-5xl h-[88vh] rounded-xl p-10'}
                `}
            >
                {/* BOUTON FULLSCREEN */}
                <div className="top-6 left-6 z-10 absolute">
                    <button
                        type="button"
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        className="group flex justify-center items-center bg-orange-600 px-3 py-2 rounded-xl overflow-hidden text-white text-sm transition-all duration-300"
                    >
                        <i className={`fa-solid ${isFullscreen ? 'fa-minimize' : 'fa-maximize'}`}></i>
                        <span className="opacity-0 group-hover:opacity-100 group-hover:ml-2 max-w-0 group-hover:max-w-xs whitespace-nowrap transition-all duration-700 ease-in-out">
                            {isFullscreen ? 'Quitter le mode plein écran' : 'Plein écran'}
                        </span>
                    </button>
                </div>

                {/* BOUTON FERMER */}
                <div className="top-6 right-6 z-10 absolute">
                    <button
                        type="button"
                        onClick={() => handleClose()}
                        className="group flex justify-center items-center bg-red-500 px-3 py-2 rounded-xl overflow-hidden text-white text-sm transition-all duration-300"
                    >
                        <i className="fa-solid fa-xmark fa-xl"></i>
                        <span className="opacity-0 max-w-0 whitespace-nowrap transition-all duration-700 ease-in-out">
                            Fermer
                        </span>
                    </button>
                </div>

                {/* HEADER */}
                <div className="text-center">
                    <h2 className="mt-5 font-bold text-gray-900 text-3xl">
                        {fileName}
                    </h2>
                    <h3 className="mt-3 text-gray-400 text-sm">
                        {fileDate
                            ? `Date du fichier: ${new Date(fileDate).toLocaleDateString('fr-FR')}`
                            : ''}
                    </h3>
                </div>

                {/* ACTIONS */}
                <div className="flex justify-end gap-4 mt-4">
                    <button
                        onClick={handleDownload}
                        className="flex justify-center bg-orange-600 hover:bg-orange-500 px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-700 focus:ring-offset-2 w-2 text-white text-sm transition duration-300 ease-in-out"
                    >
                        <i className="fa-solid fa-download"></i>
                    </button>
                    <button
                        onClick={handlePrint}
                        className="flex justify-center bg-orange-600 hover:bg-orange-500 px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-700 focus:ring-offset-2 w-2 text-white text-sm transition duration-300 ease-in-out"
                    >
                        <i className="fa-solid fa-print"></i>
                    </button>
                </div>

                {/* CONTENU */}
                <div className="flex-grow mt-5 overflow-auto" ref={viewerRef}>
                    {renderDocumentContent ? renderDocumentContent() : <p>Chargement du contenu...</p>}
                </div>
            </div>
        </div>
    );
};

export default DocumentViewerModal;
